import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs';
import ToastMessage from '../../../../../common/ToastMessage';
import CustomDataGrid from '../../../../../common/CustomDataGrid';
import useRFQ from '../../../../../store/ReactQueries/procurements/requestForQuotation.ts/useRFQ';
import useDeleteRFQ from '../../../../../store/ReactQueries/procurements/requestForQuotation.ts/useDeleteRFQ';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../../utils/constants';
import { FieldErrors } from '../../../../../domain/helpers/Errors';
import { useLanguageStore } from '../../../../../store/useLanguageStore';
import { getRFQStatusString, RFQStatus } from '../../../../../enums/RFQStatus';
import { RFQInterface } from '../../../../../models/ProcurementModule/RequestForQuotation';
import { ReactComponent as PDFLogo } from '../../../../../images/pdf-icon.svg';

export const RFQ = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [form, setForm] = useState<RFQInterface>({
        id: '',
        code: '',
        dueDate: '',
        quantity: 1,
        currency: '',
        description: '',
        unitMeasure: '',
        vendors: [],
        isUrgent: false,
        status: RFQStatus.Open,
    });
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });
    let { data: requestForQuotations, error: requestForQuotationsError, isLoading } = useRFQ(page, pageSize);
    const deleteRFQ = useDeleteRFQ(page, pageSize);

    useEffect(() => {
        if (requestForQuotationsError) ToastMessage.Failed(`Error: ${requestForQuotationsError.message}`);
    }, [requestForQuotationsError]);

    const handleDownloadPDF = async (id: string, documentUrl: string) => {
        if (id && documentUrl) {
            try {
                const link = document.createElement("a");
                link.href = documentUrl;
                link.download = "document.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

            } catch (error) {
                console.error("Error downloading PDF:", error);
            }
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'code', headerName: 'Code', type: "string", editable: false, width: 120 },
        { field: 'description', headerName: 'Description', type: "string", editable: false, width: 200 },
        { field: 'quantity', headerName: 'Quantity', type: "string", editable: false, width: 100 },
        { field: 'currency', headerName: 'Currency', type: "string", editable: false, width: 80 },
        { field: 'unitMeasure', headerName: 'UnitMeasure', type: "string", editable: false, width: 80 },
        {
            field: "dueDate",
            headerName: "DueDate",
            type: "string",
            editable: false,
            width: 100,
            renderCell: ({ row }) => (
                <span>
                    {
                        row.duedate ?? row.dueDate
                            ? dayjs(row.duedate ?? row.dueDate).format("DD/MM/YYYY")
                            : undefined
                    }
                </span>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            type: "string",
            editable: false,
            width: 100,
            renderCell: ({ row }) => (
                <span title={row.status}>{getRFQStatusString(row.status)}</span>
            )
        },
        { field: 'budget', headerName: 'Budget', type: "number", editable: false, width: 120 },
        {
            field: 'vendors',
            headerName: 'Vendors',
            type: "string",
            editable: false,
            width: 300,
            renderCell: ({ row }) => {
                const vendors = row && row.vendors ? row.vendors.map((v: any) => v.vendor.name).join(',') : '';
                return <div className="d-flex align-items-center justify-content-between">
                    <div
                        title={vendors}
                        className="text-truncate"
                        style={{ maxWidth: 300 }}
                    >
                        {
                            row.vendors && row.vendors.length > 0
                                ? <span>{vendors}</span>
                                : ''
                        }
                    </div>
                </div>
            }
        },
        { field: 'isUrgent', headerName: 'Urgent', type: "boolean", editable: false, width: 80 },
        {
            field: 'documentUrl',
            headerName: 'DocumentTemplate',
            type: "string",
            editable: false,
            width: 80,
            renderCell: ({ row }) => {
                return row.documentUrl
                    ? <div className="d-flex align-items-center justify-content-between">
                        <div
                            className='pdf-icon-download'
                            role='button'
                            title={'Download template'}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownloadPDF(row.id, row.documentUrl)
                            }}
                        >
                            <PDFLogo width="30" height="30" />
                        </div>
                    </div>
                    : null
            }
        },
    ];

    const addItems = () => {
        setForm({ ...form, id: '', description: '', code: '' });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleDelete = async (id: string, confirmation: boolean = true) => {
        const proceed = confirmation
            ? await ToastMessage.Confirm(language['ToastDeleteItem'])
            : true;

        if (proceed) {
            deleteRFQ.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    const showColumnModal: string[] = ['code', 'description', 'dueDate', 'isUrgent'];
    const hideColumns: string[] = ['id', 'purchaseOrderTypeId', 'budget'];

    return <div className='d-flex flex-row transition-wrapper'>
        <div className="content">
            <CustomDataGrid
                name={language['RequestForQuotations']}
                newAddComponent="RequestForQuotations"
                rows={requestForQuotations || []}
                form={form}
                page={page}
                modal={modal}
                errors={errors}
                columns={columns}
                loading={isLoading}
                pageSize={pageSize}
                hideColumns={hideColumns}
                showColumnModal={showColumnModal}
                setPage={setPage}
                setForm={setForm}
                setModal={setModal}
                addItems={addItems}
                setErrors={setErrors}
                setPageSize={setPageSize}
                handleDelete={handleDelete}
                handleSave={function (): Promise<void> {
                    throw new Error('Function not implemented.');
                }} />
        </div>
    </div>
}
export default RFQ;